import {RouterBridge, RouterBridgeInstance, ServerRouter, ServerRouterInstance} from "@/infrastructure/navigation";
import autoBind from "auto-bind";
import {proxy, ref} from "valtio";
import {watch} from "valtio/utils";

import {Coupon, type CouponInstance} from "@/features/coupon/coupon.model";
import {GuestsStepper, GuestsStepperInstance} from "@/features/rental/guests";
import {GuestsStepperConfig, GuestValidationError} from "@/features/rental/guests/guests-stepper.model";
import {InquiryForm, type InquiryFormInstance} from "@/features/rental/inquiry-form/inquiry-form.model";

import {CollapsibleRow, RentSummary} from "@/entities/booking";
import {createCheckout, fetchQuote} from "@/entities/booking/api/actions";
import type {CheckoutResponse, QuoteResponse, QuoteSuccessResponse} from "@/entities/booking/api/types";
import {fetchUnitCalendar, fetchUnitItinerary} from "@/entities/rental/api/actions";
import type {UnitCalendar, UnitItinerary} from "@/entities/rental/api/types/search.types";

import {INavigable, IServerNavigable} from "@/shared/contracts";
import {EventEmitter} from "@/shared/event-emitter";
import {CurrencyUtils, DateHelper} from "@/shared/lib";
import {BaseValtioModel} from "@/shared/models/base-valtio-model";
import {DateRangePicker, type DateRangePickerInstance} from "@/shared/models/form/datepickers";
import {QueryState, type QueryStateInstance} from "@/shared/services";
import {getSegmentAnonymousId} from "@/shared/services/analytics/analytics.emitters";
import {CheckAvailabilityAnalytics, type CheckAvailabilityAnalyticsInstance} from "@/shared/services/analytics/check-availability.analytics";
import {routes} from "@/shared/utils/routing/routes.config";
import {isClient} from "@/shared/utils/runtime";

export interface GuestLimits {
    maxAdults: number;
    maxChildren: number;
    maxPets: number;
    maxTotalGuests: number;
    allowsPets: boolean;
}

/** One month/year for which there are no check-out dates visible (0-based month). */
export interface NoCheckoutDatesInViewItem {
    month: number;
    year: number;
}

export interface CheckAvailabilityEvents {
    /** Emitted once when no bookable check-out is visible; payload lists month/year for which it's the case (first and/or second calendar). UI decides how to react. */
    noCheckoutDatesInView: (payload: {months: Array<NoCheckoutDatesInViewItem>}) => void;
    /** Emitted when the inquiry form drawer is closed after a successful submission. */
    inquirySubmitted: () => void;
}

/** Serializable date range for server→client transfer (strings, not Date objects) */
export interface SerializableDateRange {
    startDate: string | null;
    endDate: string | null;
}

export type BookingType = "checkout" | "inquiry";

export interface UnitMeta {
    image?: string;
    location?: string;
    rating?: number;
    reviewsCount?: number;
    checkInTime?: string;
    checkOutTime?: string;
}

export interface Payload {
    unitId: string;
    unitName?: string;
    unitMeta?: UnitMeta;
    date: SerializableDateRange;
    guests: GuestsStepperConfig;
    guestLimits?: GuestLimits;
    flexible?: boolean;
    nights?: number;
    minNights?: number;
    /** Show promo code field (only for Streamline units) */
    showCoupon?: boolean;
    unitBookingType: BookingType;
}

class CheckAvailabilityClass extends BaseValtioModel implements INavigable, IServerNavigable {
    readonly events: EventEmitter<CheckAvailabilityEvents>;

    guests: GuestsStepperInstance;
    dateRange: DateRangePickerInstance;
    coupon: CouponInstance;
    routerBridge: RouterBridgeInstance;
    serverRouter: ServerRouterInstance;

    readonly unitId: string;
    readonly unitMeta: UnitMeta;
    bookingType: BookingType = "checkout";
    readonly inquiryForm: InquiryFormInstance;
    isInquiryOpen: boolean = false;

    /** Show promo code field (only for Streamline units) */
    readonly showCoupon: boolean;

    readonly unitBookingType: BookingType;

    // Calendar query state
    readonly calendarQuery: QueryStateInstance<UnitCalendar>;

    // Itinerary query state
    readonly itineraryQuery: QueryStateInstance<UnitItinerary>;

    // Flexible dates mode
    readonly flexible: boolean;
    readonly nights: number;

    // Default minimum nights (fallback when itinerary not loaded)
    private readonly defaultMinNights: number;

    // Date availability error
    dateUnavailableError: boolean = false;

    // Minimum nights error
    minNightsError: boolean = false;

    // Guest validation errors
    guestErrors: Array<GuestValidationError> = [];

    // Quote query state
    readonly quoteQuery: QueryStateInstance<QuoteResponse>;

    // Checkout query state
    readonly checkoutQuery: QueryStateInstance<CheckoutResponse>;
    checkout: CheckoutResponse | null = null;

    // Navigation state - stays true during redirect to prevent double clicks
    isNavigating: boolean = false;

    private unwatchQuoteLoading: (() => void) | null = null;
    private readonly analytics: CheckAvailabilityAnalyticsInstance;

    constructor(payload?: Payload) {
        super();
        this.analytics = CheckAvailabilityAnalytics();
        this.events = ref(new EventEmitter<CheckAvailabilityEvents>()) as EventEmitter<CheckAvailabilityEvents>;
        this.unitId = payload?.unitId || "";
        this.unitMeta = payload?.unitMeta ?? {};
        this.inquiryForm = ref(InquiryForm({unitId: this.unitId, unitName: payload?.unitName}));
        this.showCoupon = payload?.showCoupon ?? false;
        this.unitBookingType = payload?.unitBookingType ?? "checkout";
        this.flexible = payload?.flexible ?? false;
        this.nights = payload?.nights ?? 3;
        this.defaultMinNights = payload?.minNights ?? 1;
        this.dateRange = DateRangePicker({
            min: new Date(),
            initialValue: payload?.date,
            isDateDisabled: (date) => this.isDateUnavailable(date),
            clearOnSameStartClick: true,
            // Don't use minDays here - we handle minNights validation manually
            // to show tooltip instead of disabling days
        });

        // Merge guest config with limits from API
        const guestConfig: GuestsStepperConfig = {
            ...payload?.guests,
            ...(payload?.guestLimits && {
                maxAdults: payload.guestLimits.maxAdults,
                maxChildren: payload.guestLimits.maxChildren,
                maxPets: payload.guestLimits.maxPets,
                maxTotalGuests: payload.guestLimits.maxTotalGuests,
                allowsPets: payload.guestLimits.allowsPets,
            }),
        };
        this.guests = GuestsStepper(guestConfig);
        this.coupon = Coupon({
            errors: {
                noDates: {type: "no_dates", messageKey: "promoAppliesWhenDatesSelected"},
            },
        });
        this.routerBridge = RouterBridge();
        this.serverRouter = ServerRouter();

        // Initialize calendar query state
        this.calendarQuery = QueryState<UnitCalendar>(async () => {
            return fetchUnitCalendar(this.unitId);
        });

        // Initialize itinerary query state
        this.itineraryQuery = QueryState<UnitItinerary>(async () => {
            const {startDate} = this.dateRange.value;
            if (!startDate) {
                throw new Error("Start date is required for itinerary");
            }
            const checkIn = this.formatDateToCalendarKey(startDate);
            return fetchUnitItinerary(this.unitId, checkIn);
        });

        // Initialize quote query state with debounce
        this.quoteQuery = QueryState<QuoteResponse>(
            async (signal) => {
                const hadCouponCode = !!this.coupon.getValue();
                try {
                    const result = await this.fetchQuoteData(signal);
                    if (result.status === "unavailable") {
                        this.dateUnavailableError = true;
                        const {startDate, endDate} = this.dateRange.value;
                        this.analytics.trackQuoteError(
                            this.unitId,
                            startDate ? this.formatDateToCalendarKey(startDate) : "",
                            endDate ? this.formatDateToCalendarKey(endDate) : "",
                            this.guests.adults.value,
                            this.guests.children.value,
                            this.guests.pets.value,
                        );
                        return result;
                    }

                    this.dateUnavailableError = false;
                    this.syncCouponStatusFromQuote(result, hadCouponCode);
                    this.analytics.trackQuote(result);
                    this.bookingType = result.quote.inquiryOnly ? "inquiry" : "checkout";
                    return result;
                } catch (error) {
                    // Don't show error for aborted requests
                    if (error instanceof Error && error.name === "AbortError") {
                        throw error;
                    }

                    // Show unavailable alert for booking unavailable errors
                    this.dateUnavailableError = true;
                    const {startDate, endDate} = this.dateRange.value;
                    this.analytics.trackQuoteError(
                        this.unitId,
                        startDate ? this.formatDateToCalendarKey(startDate) : "",
                        endDate ? this.formatDateToCalendarKey(endDate) : "",
                        this.guests.adults.value,
                        this.guests.children.value,
                        this.guests.pets.value,
                    );

                    throw error;
                }
            },
            {debounceDelay: 500},
        );

        // Initialize checkout query state
        this.checkoutQuery = QueryState<CheckoutResponse>(async () => {
            const result = await this.createCheckoutSession();
            this.checkout = result;
            return result;
        });

        // Validate initial guest values
        this.validateGuests();
    }

    /**
     * Fetch quote data from API
     */
    private async fetchQuoteData(signal: AbortSignal): Promise<QuoteResponse> {
        const {startDate, endDate} = this.dateRange.value;

        if (!startDate || !endDate) {
            throw new Error("Dates are required for quote");
        }

        if (signal.aborted) {
            throw new DOMException("Aborted", "AbortError");
        }

        const result = await fetchQuote({
            unitId: this.unitId,
            checkIn: this.formatDateToCalendarKey(startDate),
            checkOut: this.formatDateToCalendarKey(endDate),
            adults: this.guests.adults.value,
            children: this.guests.children.value,
            pets: this.guests.pets.value,
            couponCode: this.coupon.getValue(),
            analytics: {
                anonymousId: getSegmentAnonymousId(),
            },
        });

        if (signal.aborted) {
            throw new DOMException("Aborted", "AbortError");
        }

        return result;
    }

    /**
     * Update coupon status from quote response.
     * Called after a successful quote fetch to reflect whether the coupon was applied.
     */
    private syncCouponStatusFromQuote(result: QuoteSuccessResponse, hadCouponCode: boolean): void {
        if (hadCouponCode) {
            const discount = result.quote.couponDiscount;
            if (discount != null && discount > 0) {
                this.coupon.setApplied();
            } else {
                this.coupon.setError("promoCodeInvalid");
            }
        } else {
            this.coupon.resetStatus();
        }
    }

    /**
     * Check if we can fetch quote (dates and guests are valid)
     */
    get canFetchQuote(): boolean {
        const {startDate, endDate} = this.dateRange.value;
        return !!(startDate && endDate && !this.dateUnavailableError && !this.minNightsError && !this.hasGuestErrors);
    }

    /**
     * Fetch quote if dates and guests are valid.
     * Cancels any in-flight or debounced request so the new one reflects current state.
     */
    private fetchQuoteIfReady(): void {
        if (this.canFetchQuote) {
            this.quoteQuery.cancel();
            void this.quoteQuery.execute();
        } else {
            this.quoteQuery.reset();
        }
    }

    /**
     * Get calendar data from query
     */
    get calendar(): UnitCalendar | null {
        return this.calendarQuery.data ?? null;
    }

    /**
     * Get itinerary data from query
     */
    get itinerary(): UnitItinerary | null {
        return this.itineraryQuery.data ?? null;
    }

    /**
     * Get quote data from query
     */
    get quote(): QuoteSuccessResponse | null {
        const response = this.quoteQuery.data;
        return response?.status === "available" ? response : null;
    }

    /**
     * Get effective start date (null if itinerary is loading or not yet loaded)
     */
    private get effectiveStartDate(): Date | null {
        const {startDate, endDate} = this.dateRange.value;
        const isItineraryLoading = this.itineraryQuery.isPending;
        const isWaitingForItinerary = startDate && !endDate && !this.itinerary;
        return isItineraryLoading || isWaitingForItinerary ? null : startDate;
    }

    /**
     * Get minimum nights from itinerary (nights to first bookable checkout)
     */
    get minNights(): number {
        const {startDate} = this.dateRange.value;
        if (!this.itinerary || !startDate) return this.defaultMinNights;

        const firstKey = this.getFirstBookableCheckoutKey();
        if (!firstKey) return this.defaultMinNights;

        return this.getNightsCount(startDate, this.parseDateKey(firstKey));
    }

    /**
     * Check if date is unavailable for booking based on calendar data and itinerary
     */
    private isDateUnavailable(date: Date): boolean {
        const dateNormalized = this.normalizeDate(date);
        const today = this.normalizeDate(new Date());

        // Dates before today are unavailable
        if (dateNormalized < today) return true;

        // If calendar not loaded yet, block all days
        if (!this.calendar) return true;

        const key = this.formatDateToCalendarKey(date);
        const dayInfo = this.calendar[key];
        if (!dayInfo) return true;

        const {startDate, endDate} = this.dateRange.value;

        // No start date or waiting for itinerary - check-in mode
        if (!this.effectiveStartDate) {
            return !(dayInfo.bookable || dayInfo.checkOut);
        }

        // Start date selected, no end date, itinerary loaded - check-out mode
        if (this.effectiveStartDate && !endDate && this.itinerary) {
            // Allow clicking on start date again
            if (dateNormalized.getTime() === this.effectiveStartDate.getTime()) return false;

            // Day in itinerary and bookable - available
            const itineraryItem = this.itinerary[key];
            if (itineraryItem?.bookable) return false;

            // Day between start and first checkout - available (for tooltip)
            const firstKey = this.getFirstBookableCheckoutKey();
            if (firstKey) {
                const firstCheckout = this.parseDateKey(firstKey);
                if (dateNormalized > this.effectiveStartDate && dateNormalized < firstCheckout) return false;
            }

            return true;
        }

        // Both dates selected - check itinerary
        if (startDate && endDate && this.itinerary) {
            const itineraryItem = this.itinerary[key];
            if (itineraryItem?.bookable) return false;
        }

        return !(dayInfo.bookable || dayInfo.checkOut);
    }

    /**
     * Format date to calendar key format (YYYY-MM-DD)
     */
    private formatDateToCalendarKey(date: Date): string {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
    }

    /**
     * Parse date from calendar key format (YYYY-MM-DD)
     */
    private parseDateKey(key: string): Date {
        const [year, month, day] = key.split("-").map(Number);
        return new Date(year, month - 1, day);
    }

    /**
     * Normalize date (set time to 00:00:00)
     */
    private normalizeDate(date: Date): Date {
        const normalized = new Date(date);
        normalized.setHours(0, 0, 0, 0);
        return normalized;
    }

    /**
     * Get first bookable checkout key from itinerary
     */
    private getFirstBookableCheckoutKey(): string | null {
        if (!this.itinerary) return null;

        const bookableKeys = Object.keys(this.itinerary)
            .filter((k) => this.itinerary![k].bookable)
            .sort();

        return bookableKeys.length > 0 ? bookableKeys[0] : null;
    }

    /**
     * If check-in is selected and itinerary is loaded but the first bookable check-out date
     * is not visible, emit noCheckoutDatesInView once with month/year for which it's the case.
     * Pushes first calendar and/or second; UI decides how to react per item.
     */
    private checkAndEmitRequestCalendarNextMonth(): void {
        const {startDate, endDate} = this.dateRange.value;
        if (!startDate || endDate || !this.itinerary) return;

        const firstKey = this.getFirstBookableCheckoutKey();
        if (!firstKey) return;

        const firstCheckoutDate = this.normalizeDate(this.parseDateKey(firstKey));
        const lastDayFirst = new Date(this.dateRange.viewYear, this.dateRange.viewMonth + 1, 0);
        const lastDaySecond = new Date(this.dateRange.secondViewYear, this.dateRange.secondViewMonth + 1, 0);

        const months: Array<NoCheckoutDatesInViewItem> = [];
        if (firstCheckoutDate > lastDayFirst) {
            months.push({month: this.dateRange.viewMonth, year: this.dateRange.viewYear});
        }
        if (firstCheckoutDate > lastDaySecond) {
            months.push({month: this.dateRange.secondViewMonth, year: this.dateRange.secondViewYear});
        }
        if (months.length > 0) {
            this.events.emit("noCheckoutDatesInView", {months});
        }
    }

    /**
     * Create checkout session via API
     */
    private async createCheckoutSession(): Promise<CheckoutResponse> {
        const {startDate, endDate} = this.dateRange.value;

        if (!startDate || !endDate) {
            throw new Error("Dates are required for checkout");
        }

        return createCheckout({
            unitId: this.unitId,
            checkIn: this.formatDateToCalendarKey(startDate),
            checkOut: this.formatDateToCalendarKey(endDate),
            adults: this.guests.adults.value,
            children: this.guests.children.value,
            pets: this.guests.pets.value,
            couponCode: this.coupon.getValue(),
        });
    }

    openInquiry(): void {
        const {startDate, endDate} = this.dateRange.value;
        const nights = startDate && endDate ? this.getNightsCount(startDate, endDate) : 0;

        if (!this.quote) {
            return;
        }

        this.inquiryForm.setBookingInfo({
            pricing: {quote: this.quote.quote, nights},
            checkIn: startDate ? startDate.toISOString().slice(0, 10) : null,
            checkOut: endDate ? endDate.toISOString().slice(0, 10) : null,
            checkInTime: this.unitMeta.checkInTime,
            checkOutTime: this.unitMeta.checkOutTime,
            adults: this.guests.adults.value,
            children: this.guests.children.value,
            pets: this.guests.pets.value,
            unitImage: this.unitMeta.image,
            unitLocation: this.unitMeta.location,
            rating: this.unitMeta.rating,
            reviewsCount: this.unitMeta.reviewsCount,
        });

        this.isInquiryOpen = true;
    }

    closeInquiry(): void {
        this.isInquiryOpen = false;
        if (this.inquiryForm.submitFeedback.isSuccess) {
            this.events.emit("inquirySubmitted");
        }
    }

    private resetInquiryIfSubmitted(): void {
        if (this.inquiryForm.submitFeedback.isSuccess) {
            this.inquiryForm.reset();
        }
    }

    /**
     * Submit - behaviour depends on bookingType:
     * - "inquiry": opens inquiry form modal
     * - "checkout": creates checkout session and navigates to checkout page
     */
    async submit(): Promise<void> {
        if (this.bookingType === "inquiry") {
            this.openInquiry();
            return;
        }

        if (!this.canSubmit) return;

        try {
            await this.checkoutQuery.execute();

            if (this.checkout?.id) {
                this.analytics.trackBeginCheckout(this.checkout);
                // Keep loading state during navigation
                this.isNavigating = true;
                this.serverRouter.navigate(routes.checkout.url(this.checkout.id));
            }
        } catch (error) {
            console.error("Failed to create checkout:", error);
        }
    }

    /**
     * Check if we can submit (has dates, quote, no errors)
     */
    get canSubmit(): boolean {
        const {startDate, endDate} = this.dateRange.value;
        return !!(
            startDate &&
            endDate &&
            this.quote &&
            !this.dateUnavailableError &&
            !this.minNightsError &&
            !this.hasGuestErrors &&
            !this.isSubmitting
        );
    }

    /**
     * Check if checkout is in progress (including navigation)
     */
    get isSubmitting(): boolean {
        return this.checkoutQuery.isPending || this.isNavigating;
    }

    /**
     * Check if action button should show loading state
     * Loading when: submitting checkout OR quote is loading OR waiting for quote
     */
    get isActionLoading(): boolean {
        const isWaitingForQuote = this.hasDates && !this.quote && !this.hasDateError;
        return this.isSubmitting || this.isQuoteLoading || isWaitingForQuote;
    }

    /**
     * Check if dates are selected
     */
    get hasDates(): boolean {
        return !!(this.dateRange.value.startDate && this.dateRange.value.endDate);
    }

    /**
     * Check if flexible nights mode is active
     */
    get isFlexibleMode(): boolean {
        return this.flexible && this.nights > 0;
    }

    /**
     * Check if there's a date-related error
     */
    get hasDateError(): boolean {
        return this.minNightsError || this.dateUnavailableError;
    }

    /**
     * Get date error message key for display
     */
    get dateErrorMessage(): string | null {
        if (this.minNightsErrorMessage) return this.minNightsErrorMessage;
        if (this.dateUnavailableError) return "selectedDatesUnavailable";
        return null;
    }

    /**
     * Get formatted date range string (e.g., "Dec 15 - Dec 20")
     */
    get formattedDateRange(): string {
        return DateHelper.formatDateRange(this.dateRange.value.startDate, this.dateRange.value.endDate);
    }

    private navigate(): void {
        const params = [this.dateRange.getQueryParam("checkIn", "checkOut"), this.guests.getQueryParam()].join("&");

        this.routerBridge.replaceSearchParams(params, {scroll: false});
    }

    /**
     * Apply date validation or flexible date selection depending on mode.
     * Requires calendar to already be loaded. Called from loadCalendar() and onUrlChange().
     */
    private applyCalendarValidation(): void {
        if (this.isFlexibleMode) {
            this.findAndSelectFirstAvailableNights();
        } else {
            this.validateSelectedDates();
        }
    }

    /**
     * Load calendar availability from API (called on client-side)
     */
    async loadCalendar(): Promise<void> {
        if (!this.unitId || this.calendar) return;

        try {
            await this.calendarQuery.execute();
            // Force calendar re-render after data loaded
            this.dateRange.forceUpdate();
            // Navigate calendar to first available month
            this.navigateToFirstAvailableMonth();

            // Apply date validation or flexible date selection based on mode
            this.applyCalendarValidation();

            // In flexible mode dates just changed — fetch quote with the new window.
            // Non-flexible quote is already running in parallel (started in init()).
            if (this.isFlexibleMode) {
                this.fetchQuoteIfReady();
            }

            const {startDate, endDate} = this.dateRange.value;
            if (startDate && !endDate) {
                void this.loadItinerary();
            }
        } catch (error) {
            console.error("Failed to load calendar:", error);
        }
    }

    /**
     * Load itinerary for selected check-in date
     * Fetches valid check-out dates respecting minimum stay requirements
     */
    async loadItinerary(): Promise<void> {
        if (!this.unitId || !this.dateRange.value.startDate) {
            return;
        }

        try {
            await this.itineraryQuery.execute();
            // Trigger calendar refresh to show updated availability
            this.dateRange.forceUpdate();
            // Validate minNights now that itinerary is loaded
            this.validateMinNights();
            // If first bookable check-out is not in current two-month view, emit so consumer can advance (e.g. desktop)
            this.checkAndEmitRequestCalendarNextMonth();
        } catch (error) {
            console.error("Failed to load itinerary:", error);
            this.itineraryQuery.reset();
        }
    }

    /**
     * Navigate calendar view to the first month with available dates
     */
    private navigateToFirstAvailableMonth(): void {
        if (!this.calendar) return;

        // If dates are already selected, don't change the view
        if (this.dateRange.value.startDate) return;

        const firstAvailableDate = this.findFirstAvailableDate();

        if (firstAvailableDate) {
            this.dateRange.setViewMonth(firstAvailableDate.getMonth(), firstAvailableDate.getFullYear());
        }
    }

    /**
     * Find the first available date in the calendar
     */
    private findFirstAvailableDate(): Date | null {
        if (!this.calendar) return null;

        // Get all dates from calendar and sort them
        const dates = Object.keys(this.calendar)
            .filter((key) => this.calendar![key].bookable)
            .map((key) => new Date(key))
            .sort((a, b) => a.getTime() - b.getTime());

        // Find first date that's not in the past
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        for (const date of dates) {
            if (date >= today) {
                return date;
            }
        }

        return null;
    }

    /**
     * Check if itinerary is loading
     */
    get isItineraryLoading(): boolean {
        return this.itineraryQuery.isPending;
    }

    /**
     * Find first available consecutive nights starting from checkIn date or today
     * and auto-select them in the calendar
     */
    private findAndSelectFirstAvailableNights(): void {
        if (!this.calendar || this.nights <= 0) return;

        // Start searching from checkIn date or today
        const startSearchDate = this.dateRange.value.startDate || new Date();
        const endSearchDate = this.dateRange.value.endDate;

        // Find first available consecutive nights within the date range
        const result = this.findConsecutiveAvailableNights(startSearchDate, endSearchDate, this.nights);

        if (result) {
            // Auto-select the found dates silently (don't trigger events/navigate)
            this.dateRange.setValue({startDate: result.startDate, endDate: result.endDate}, true);
            this.dateUnavailableError = false;
        } else {
            // No available nights found - show error
            this.dateUnavailableError = true;
        }
    }

    /**
     * Find consecutive available nights within date range
     */
    private findConsecutiveAvailableNights(
        searchStart: Date,
        searchEnd: Date | null,
        requiredNights: number,
    ): {startDate: Date; endDate: Date} | null {
        if (!this.calendar) return null;

        // Search up to 365 days from start or until searchEnd
        const maxSearchDays = 365;
        const current = new Date(searchStart);
        const maxDate = searchEnd || new Date(searchStart.getTime() + maxSearchDays * 24 * 60 * 60 * 1000);

        while (current <= maxDate) {
            // Check if we can start a consecutive sequence from this date
            const endDate = this.checkConsecutiveAvailability(current, requiredNights);

            if (endDate) {
                return {startDate: new Date(current), endDate};
            }

            // Move to next day
            current.setDate(current.getDate() + 1);
        }

        return null;
    }

    /**
     * Check if requiredNights consecutive days starting from startDate are all available
     * Returns the end date if available, null otherwise
     */
    private checkConsecutiveAvailability(startDate: Date, requiredNights: number): Date | null {
        const current = new Date(startDate);

        for (let i = 0; i <= requiredNights; i++) {
            if (this.isDateUnavailable(current)) {
                return null;
            }
            if (i < requiredNights) {
                current.setDate(current.getDate() + 1);
            }
        }

        return current;
    }

    /**
     * Validate selected dates against calendar availability
     * Set error if dates are unavailable (keep dates selected)
     */
    private validateSelectedDates(): void {
        const {startDate, endDate} = this.dateRange.value;

        // Check if start date is unavailable
        const isStartUnavailable = startDate && this.isDateUnavailable(startDate);

        // Check if end date is valid checkout from itinerary
        let isEndUnavailable = false;
        if (endDate && this.itinerary) {
            const endKey = this.formatDateToCalendarKey(endDate);
            const itineraryItem = this.itinerary[endKey];
            // End date must be in itinerary and bookable
            isEndUnavailable = !itineraryItem || !itineraryItem.bookable;
        }

        this.dateUnavailableError = isStartUnavailable || isEndUnavailable;
    }

    /**
     * Check if there are unavailable dates between start and end
     */
    private hasUnavailableDatesInRange(startDate: Date | null, endDate: Date | null): boolean {
        if (!startDate || !endDate || !this.calendar) return false;

        const current = new Date(startDate);
        while (current <= endDate) {
            if (this.isDateUnavailable(current)) {
                return true;
            }
            current.setDate(current.getDate() + 1);
        }
        return false;
    }

    /**
     * Clear the date unavailable error
     */
    clearDateError(): void {
        this.dateUnavailableError = false;
        this.minNightsError = false;
    }

    /**
     * Validate minNights requirement
     */
    private validateMinNights(): void {
        const {startDate, endDate} = this.dateRange.value;

        // Don't validate if itinerary is not loaded yet (minNights depends on it)
        if (!startDate || !endDate || !this.itinerary || this.minNights <= 1) {
            this.minNightsError = false;
            return;
        }

        const nightsCount = this.getNightsCount(startDate, endDate);
        this.minNightsError = nightsCount < this.minNights;
    }

    /**
     * Get minNights error message key
     */
    get minNightsErrorMessage(): string | null {
        if (!this.minNightsError) return null;
        return "minNightsNotMet";
    }

    /**
     * Get additional CSS class for a calendar day
     */
    getDayClassName(date: Date): string | null {
        const {startDate, endDate} = this.dateRange.value;
        const dateNorm = this.normalizeDate(date);

        // No start date - highlight all days not available for check-in (same as isDateUnavailable)
        if (!this.effectiveStartDate && this.calendar) {
            const key = this.formatDateToCalendarKey(date);
            const dayInfo = this.calendar[key];
            if (!dayInfo || !dayInfo.checkIn) {
                return "no-checkin";
            }
        }

        // Start date selected, itinerary loaded - highlight days that are not valid checkouts
        if (this.effectiveStartDate && !endDate && this.itinerary) {
            // Skip startDate itself
            if (dateNorm.getTime() === this.normalizeDate(startDate!).getTime()) {
                return null;
            }

            // Check if day is a valid checkout in itinerary
            const key = this.formatDateToCalendarKey(date);
            const itineraryItem = this.itinerary[key];

            // If NOT in itinerary or not bookable - it's no-checkout
            if (!itineraryItem || !itineraryItem.bookable) {
                // Only mark days after startDate
                if (dateNorm > this.normalizeDate(startDate!)) {
                    return "no-checkout";
                }
            }
        }

        // Both dates selected - show classes for days outside range
        if (startDate && endDate) {
            const startNorm = this.normalizeDate(startDate);
            const endNorm = this.normalizeDate(endDate);

            // In range - no class
            if (dateNorm >= startNorm && dateNorm <= endNorm) return null;

            // Outside range - show checkout-only
            if (this.isCheckoutOnlyDay(date)) return "no-checkin";
        }

        return null;
    }

    /**
     * Check if date is checkout-only day (checkOut: true, checkIn: false)
     */
    private isCheckoutOnlyDay(date: Date): boolean {
        if (!this.calendar) return false;

        const key = this.formatDateToCalendarKey(date);
        const dayInfo = this.calendar[key];

        return dayInfo ? dayInfo.checkOut && !dayInfo.checkIn : false;
    }

    /**
     * Check if date is checkin-only day (checkIn: true, checkOut: false)
     */
    private isCheckinOnlyDay(date: Date): boolean {
        if (!this.calendar) return false;

        const key = this.formatDateToCalendarKey(date);
        const dayInfo = this.calendar[key];

        return dayInfo ? dayInfo.bookable && dayInfo.checkIn && !dayInfo.checkOut : false;
    }

    /**
     * Get tooltip for a calendar day (used for minNights validation)
     * Returns translation key with optional params
     */
    getDayTooltip(date: Date): {key: string; params?: Record<string, number>} | null {
        const {startDate, endDate} = this.dateRange.value;

        // Check-in mode: show label for days disabled because they're not available for check-in
        // (so gray days still show "This day is not available for check-in" on hover)
        if (!startDate && this.calendar) {
            const key = this.formatDateToCalendarKey(date);
            const dayInfo = this.calendar[key];
            if (dayInfo && !dayInfo.checkIn) {
                return {key: "dayNotAvailableCheckIn"};
            }
        }

        // Other disabled days (e.g. past, no data) – no tooltip
        if (this.isDateUnavailable(date)) {
            return null;
        }

        // If no start date selected - show tooltip for checkout-only days
        if (!startDate) {
            if (this.isCheckoutOnlyDay(date) && !this.isDateUnavailable(date)) {
                return {key: "dayNotAvailableCheckIn"};
            }
        }

        // Only show tooltip when start date is selected but end date is not
        if (!startDate || endDate) {
            return null;
        }

        // Don't show tooltip for dates before or equal to start date
        if (date <= startDate) {
            return null;
        }

        // Show tooltip for checkin-only days when selecting checkout date
        // Only show if day is checkin-only AND not disabled
        if (this.isCheckinOnlyDay(date) && !this.isDateUnavailable(date)) {
            // Find first available checkout date from itinerary
            if (this.itinerary) {
                const bookableKeys = Object.keys(this.itinerary)
                    .filter((k) => this.itinerary![k].bookable)
                    .sort();

                if (bookableKeys.length > 0) {
                    // Parse date from YYYY-MM-DD string to avoid timezone issues
                    const [year, month, day] = bookableKeys[0].split("-").map(Number);
                    const firstAvailableCheckoutDate = new Date(year, month - 1, day);
                    const minNights = this.getNightsCount(startDate, firstAvailableCheckoutDate);
                    return {
                        key: "mustBookAtLeastNights",
                        params: {minNights},
                    };
                }
            }

            return {key: "dayNotAvailableCheckOut"};
        }

        // If itinerary is loaded, check for minimum stay requirements
        if (this.itinerary) {
            const key = this.formatDateToCalendarKey(date);
            const itineraryItem = this.itinerary[key];

            // Date is in itinerary and bookable - valid checkout, no tooltip
            if (itineraryItem && itineraryItem.bookable) {
                return null;
            }

            // Date is NOT in itinerary or not bookable - show minNights tooltip
            // (these are days between startDate and first valid checkout)
            const firstKey = this.getFirstBookableCheckoutKey();
            if (firstKey) {
                const firstCheckout = this.parseDateKey(firstKey);
                const dateNorm = this.normalizeDate(date);
                const startNorm = this.normalizeDate(startDate);

                // If date is between start and first checkout - show minNights
                if (dateNorm > startNorm && dateNorm < firstCheckout) {
                    const minNights = this.getNightsCount(startDate, firstCheckout);
                    return {
                        key: "mustBookAtLeastNights",
                        params: {minNights},
                    };
                }
            }

            return null;
        }

        // Fallback to old logic when itinerary is not loaded
        // Check if range contains unavailable dates
        if (this.hasUnavailableDatesInRange(startDate, date)) {
            return {key: "datesSpanUnavailable"};
        }

        // Check minNights requirement
        if (this.minNights > 1) {
            const daysDiff = this.getNightsCount(startDate, date);

            // If the range would be less than minNights, show tooltip
            if (daysDiff > 0 && daysDiff < this.minNights) {
                return {
                    key: "mustBookAtLeastNights",
                    params: {minNights: this.minNights},
                };
            }
        }

        return null;
    }

    /**
     * Validate guest counts against limits
     */
    validateGuests(): void {
        const result = this.guests.validate();
        this.guestErrors = result.errors;
    }

    /**
     * Check if there are any guest errors
     */
    get hasGuestErrors(): boolean {
        return this.guestErrors.length > 0;
    }

    /**
     * Get fees breakdown from quote
     */
    get feesAmounts(): Array<CollapsibleRow> {
        if (!this.quote) {
            return [];
        }

        return this.quote.quote.fees.breakdown.map((fee) => ({
            label: fee.label,
            amount: this.formatCurrency(fee.amount),
        }));
    }

    /**
     * Get rent summary (price x nights)
     * Returns translation key with params for label
     */
    get rentSummary(): RentSummary | null {
        if (!this.quote) return null;

        const {startDate, endDate} = this.dateRange.value;
        if (!startDate || !endDate) return null;

        const nights = this.getNightsCount(startDate, endDate);
        const rentTotal = this.quote.quote.rent.total;
        const pricePerNight = nights > 0 ? rentTotal / nights : 0;

        return {
            labelKey: "rentSummary",
            labelParams: {
                price: this.formatCurrency(pricePerNight),
                nights: nights,
            },
            amount: this.formatCurrency(rentTotal),
        };
    }

    /**
     * Get total price without taxes (taxes shown separately)
     * total - taxes (coupon discount already applied in quote.total)
     */
    get totalPrice(): string | null {
        if (!this.quote) return null;
        const q = this.quote.quote;
        return this.formatCurrency(q.total - q.taxes.total);
    }

    /**
     * Get taxes breakdown from quote
     */
    get taxesAmounts(): Array<CollapsibleRow> {
        if (!this.quote) return [];
        return this.quote.quote.taxes.breakdown.map((tax) => ({
            label: tax.label,
            amount: this.formatCurrency(tax.amount),
        }));
    }

    /**
     * Get total price including taxes
     */
    get totalWithTax(): string | null {
        if (!this.quote) return null;
        return this.formatCurrency(this.quote.quote.total);
    }

    /**
     * Get coupon discount as formatted negative amount (display only)
     * Returns null if no discount or coupon not applied
     */
    get couponDiscount(): string | null {
        const discount = this.quote?.quote.couponDiscount;
        if (!discount || !this.coupon.isApplied) return null;
        return this.formatCurrency(-discount);
    }

    /**
     * Check if quote is loading
     */
    get isQuoteLoading(): boolean {
        return this.quoteQuery.isPending;
    }

    /**
     * Calculate nights between two dates
     */
    private getNightsCount(startDate: Date, endDate: Date): number {
        // Normalize dates to midnight to avoid timezone/time issues
        const start = new Date(startDate);
        start.setHours(0, 0, 0, 0);
        const end = new Date(endDate);
        end.setHours(0, 0, 0, 0);

        const timeDiff = end.getTime() - start.getTime();
        return Math.round(timeDiff / (1000 * 60 * 60 * 24));
    }

    /**
     * Format amount as currency
     */
    private formatCurrency(amount: number): string {
        return CurrencyUtils.formatUSD(amount, false);
    }

    onUrlChange(_pathname: string, searchParams: URLSearchParams): void {
        this.dateRange.setFromQueryParams(searchParams, {start: "checkIn", end: "checkOut"});
        this.guests.setFromQueryParams(searchParams);

        // Load itinerary if start date is set but end date is not
        const {startDate, endDate} = this.dateRange.value;
        if (startDate && !endDate) {
            void this.loadItinerary();
        } else {
            this.itineraryQuery.reset();
        }

        // Validate guests after URL change
        this.validateGuests();

        // Validate minNights requirement
        this.validateMinNights();

        // Re-validate dates if calendar is loaded
        if (this.calendar) {
            this.applyCalendarValidation();
        }
        //
        // // Fetch quote after URL params are set
        this.fetchQuoteIfReady();
    }

    init(): void {
        if (!isClient()) {
            throw new Error("CheckAvailability.init() must only be called on the client side");
        }

        // Load calendar on init (client-side).
        void this.loadCalendar();

        if (!this.isFlexibleMode) {
            this.fetchQuoteIfReady();
        }

        // Sync coupon disabled state with any active fetch
        this.unwatchQuoteLoading = watch((get) => {
            const isLoading =
                get(this.quoteQuery).isPending ||
                get(this.calendarQuery).isPending ||
                get(this.itineraryQuery).isPending ||
                get(this.checkoutQuery).isPending;
            this.coupon.setDisabled(isLoading);
        });
        this.addSubscribe(() => this.unwatchQuoteLoading?.());

        // Load itinerary when start date is selected
        this.addSubscribe(
            this.dateRange.events.on("startSelect", () => {
                // If clicked on same start date - DateRangePicker will clear it via clearOnSameStartClick
                // In this case startDate will be null, so we handle cleanup
                const {startDate} = this.dateRange.value;

                if (!startDate) {
                    // Dates were cleared by clicking on same date
                    this.clearDateError();
                    this.quoteQuery.reset();
                    this.itineraryQuery.reset();
                    this.resetInquiryIfSubmitted();
                    return;
                }

                // Clear quote when selecting new start date (previous period is reset)
                this.quoteQuery.reset();
                this.resetInquiryIfSubmitted();

                // Load itinerary for new start date
                void this.loadItinerary();
            }),
        );

        this.addSubscribe(
            this.dateRange.events.on("endSelect", (clickedDate: Date) => {
                const {startDate, endDate} = this.dateRange.value;

                // Check minNights requirement
                if (startDate && endDate && this.minNights > 1) {
                    const nightsCount = this.getNightsCount(startDate, endDate);
                    if (nightsCount < this.minNights) {
                        // Reset selection - ignore the click, keep start date
                        this.dateRange.selectStartDate(startDate);
                        return;
                    }
                }

                // Check if range contains unavailable dates
                // Use value from model, not event param, because DateRangePicker may have swapped dates
                if (startDate && endDate && this.hasUnavailableDatesInRange(startDate, endDate)) {
                    // Reset selection - make clicked date the new start date
                    this.dateRange.selectStartDate(clickedDate);
                    this.dateUnavailableError = false;
                    // Clear itinerary when resetting selection
                    this.itineraryQuery.reset();
                    return;
                }

                // Validate dates after selection
                this.validateSelectedDates();

                // Clear itinerary after both dates selected
                this.itineraryQuery.reset();

                // Fetch quote after dates selected (navigate called when fetch completes)
                this.fetchQuoteIfReady();
                this.navigate();
            }),
        );

        this.addSubscribe(
            this.dateRange.events.on("clear", () => {
                this.clearDateError();
                this.quoteQuery.reset();
                this.itineraryQuery.reset();
                this.resetInquiryIfSubmitted();
                this.navigate();
            }),
        );

        this.addSubscribe(
            this.guests.events.on("change", () => {
                // Re-validate guests on change
                this.validateGuests();
                this.navigate();
                this.resetInquiryIfSubmitted();
                // Fetch quote after guests changed (navigate called when fetch completes)
                this.fetchQuoteIfReady();
            }),
        );

        this.addSubscribe(
            this.coupon.events.on("apply", () => {
                if (!this.hasDates) {
                    this.coupon.setError("noDates");
                    return;
                }

                if (!this.canFetchQuote) {
                    this.coupon.resetStatus();
                    return;
                }

                this.fetchQuoteIfReady();
            }),
        );

        this.addSubscribe(
            this.coupon.events.on("clear", () => {
                this.fetchQuoteIfReady();
            }),
        );
    }

    override dispose(): void {
        super.dispose();
        this.quoteQuery.reset();
        this.calendarQuery.cancel();
        this.itineraryQuery.cancel();
        this.checkoutQuery.cancel();
        this.routerBridge.dispose();
        this.serverRouter.dispose();
    }
}

export type CheckAvailabilityInstance = CheckAvailabilityClass;

export function CheckAvailability(payload?: Payload): CheckAvailabilityInstance {
    return autoBind(proxy(new CheckAvailabilityClass(payload)));
}
